import { Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconBookmark } from "@tabler/icons-react";
import type { JSX } from "react";
import { useMutation } from "urql";
import ActionButton from "../../ActionButton";
import { editMutation, type TuneThreadEditMutation, type TuneThreadEditMutationVariables } from "./queries";

interface IProps {
    tuneThreadId: number;
    itemContent: string;
    llmJudge?: string | null;
    currentReferenceAnswer?: string | null;
    disabled?: boolean;
}

function SetReferenceFromItemButton({
    tuneThreadId,
    itemContent,
    llmJudge,
    currentReferenceAnswer,
    disabled,
}: IProps): JSX.Element {
    const [updateResult, executeUpdate] = useMutation<TuneThreadEditMutation, TuneThreadEditMutationVariables>(
        editMutation,
    );

    const doUpdate = async () => {
        const result = await executeUpdate({
            data: {
                tuneThreadId,
                referenceAnswer: itemContent,
                llmJudge: llmJudge ?? null,
            },
        });

        if (result.error) {
            notifications.show({
                title: "Failed to set reference answer",
                message: result.error.message,
            });
        } else {
            notifications.show({
                title: "Reference answer set",
                message: "This message was saved as the thread's reference answer.",
            });
        }
    };

    const onClick = () => {
        if (currentReferenceAnswer) {
            modals.openConfirmModal({
                title: "Override reference answer?",
                children: (
                    <Text size="sm">
                        This thread already has a reference answer. Are you sure you want to replace it with this
                        message?
                    </Text>
                ),
                labels: { confirm: "Confirm", cancel: "Cancel" },
                onConfirm: doUpdate,
            });
        } else {
            void doUpdate();
        }
    };

    return (
        <ActionButton
            onClick={onClick}
            label="Set as reference"
            icon={<IconBookmark stroke={1} />}
            disabled={disabled || updateResult.fetching}
        />
    );
}

export default SetReferenceFromItemButton;
